import { Link } from "@mui/material";

import { faqs, howItWorks, SalemRedSoxLink } from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import {
  DefaultRegistrationTerms,
  DefaultCheckoutTerms,
} from "../../default_terms_and_conditions";

import StaticSeatMap from "public/images/salemredsox/SalemRedSoxMaps.png";
import FindGamesBanner from "public/images/salemredsox/2025 Buy More Save More.jpg";

import type { TeamConfig } from "../types";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Salem_Sox.png";

const config: TeamConfig = {
  teamSchema: "salemredsox_flex",
  browserTitle: "Buy More Save More",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Salem Red Sox",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You've unlocked 20% off your tickets! Add 1 more game and receive 30% off, 2 more games and get 40% off!",
      },
      {
        id: 2,
        minSelectedAmount: 3,
        updatedAccountGroup: 1,
        discount: 30,
        customProgressBarMessage:
          "Congrats! You have unlocked 30% off your seats! Add 1 more game for 40% off your tickets. Add more games or checkout now.",
      },
      {
        id: 3,
        minSelectedAmount: 4,
        updatedAccountGroup: 2,
        discount: 40,
        customProgressBarMessage:
          "Congrats! You have unlocked 40% off your tickets! Add more games or checkout now.",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    isView3dButtonHidden: true,
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,

    customSeatMapStaticImageStyle: {
      width: "90%",
      display: "block",
      marginX: "auto",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 8,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isPerksEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl:
    "https://mlb.tickets.com/?orgid=57031&agency=MILB_MPV#/auth/login?ret=user~2Fprofile",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    authBackgroundImage: FindGamesBanner.src,
    findGamesBannerImage: FindGamesBanner.src,
    checkoutStatusPageImage: FindGamesBanner.src,
    logoImage: LogoImage + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideSectionFilter: false,
      hideAccessibilityFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:5403893333"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (540) 389-3333
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#002b5c",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,

    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),
      progressBarText:
        "Add 2 games and get 20% off, add 3 games and get 30% off, add 4 games and get 40% off your tickets!",
      progressBarAditionalText: " for 20% off!",
    },

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: (
      <>
        Your Salem Red Sox tickets are confirmed! Your tickets will be available
        within 24 hours in your <SalemRedSoxLink /> account.
      </>
    ),

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
